import { ReactNode, useId } from 'react';
import { choiceValues, fieldChecked, fieldValue, usePolarisEvents } from '../../app/components/polaris';

export type MappingFilter = { key: string; label: string; filter: ReactNode };
export type AppliedMappingFilter = { key: string; label: string; onRemove: (key: string) => void };
export type MappingSortOption = { label: string; value: string; directionLabel?: string };

type Props = {
    hasVehicleContext: boolean;
    mappedOnly: boolean;
    onMappedOnlyChange: (checked: boolean) => void;
    /** Wording for the selected-vehicle filter, e.g. "Fits this vehicle". */
    mappedOnlyLabel?: string;
    queryValue: string;
    queryPlaceholder: string;
    onQueryChange: (value: string) => void;
    onClearAll: () => void;
    filters: MappingFilter[];
    appliedFilters: AppliedMappingFilter[];
    sortOptions: MappingSortOption[];
    sortSelected: string[];
    onSort: (selected: string[]) => void;
};

const AppliedFilterChip = ({ filter }: { filter: AppliedMappingFilter }) => {
    const events = usePolarisEvents({ remove: () => filter.onRemove(filter.key) });
    return (
        <s-clickable-chip ref={events} removable accessibilityLabel={`Remove filter: ${filter.label}`}>
            {filter.label}
        </s-clickable-chip>
    );
};

/** Search, filter and sort controls for a table's filter area, in the admin index layout. */
const MappingFilters = ({
    hasVehicleContext,
    mappedOnly,
    onMappedOnlyChange,
    mappedOnlyLabel = 'Mapped to this vehicle',
    queryValue,
    queryPlaceholder,
    onQueryChange,
    onClearAll,
    filters,
    appliedFilters,
    sortOptions,
    sortSelected,
    onSort,
}: Props) => {
    const id = useId().replace(/:/g, '');
    const filterPopoverId = `pf-filters-${id}`;
    const sortPopoverId = `pf-sort-${id}`;
    // The selected-vehicle filter only applies while a vehicle is chosen.
    const showMappedOnly = hasVehicleContext && mappedOnly;
    const chips: AppliedMappingFilter[] = showMappedOnly
        ? [{ key: 'mappedOnly', label: mappedOnlyLabel, onRemove: () => onMappedOnlyChange(false) }, ...appliedFilters]
        : appliedFilters;
    const hasFilters = filters.length > 0 || hasVehicleContext;

    return (
        <s-stack gap="small-200">
            <s-grid gridTemplateColumns="1fr auto auto" gap="small-200" alignItems="center">
                <s-search-field
                    label={queryPlaceholder}
                    labelAccessibilityVisibility="exclusive"
                    placeholder={queryPlaceholder}
                    value={queryValue}
                    onInput={event => onQueryChange(fieldValue(event))}
                    onChange={event => onQueryChange(fieldValue(event))}
                />
                {hasFilters && (
                    <s-button icon="filter" commandFor={filterPopoverId}>Filter</s-button>
                )}
                <s-button icon="sort" accessibilityLabel="Sort" commandFor={sortPopoverId} />
            </s-grid>

            {hasFilters && (
                <s-popover id={filterPopoverId}>
                    <s-box padding="base">
                        <s-stack gap="base">
                            {hasVehicleContext && (
                                <s-stack gap="small-200">
                                    <s-text fontWeight="semibold">Selected vehicle</s-text>
                                    <s-checkbox
                                        label={mappedOnlyLabel}
                                        checked={mappedOnly}
                                        onChange={event => onMappedOnlyChange(fieldChecked(event))}
                                    />
                                </s-stack>
                            )}
                            {filters.map(filter => (
                                <s-stack key={filter.key} gap="small-200">
                                    <s-text fontWeight="semibold">{filter.label}</s-text>
                                    {filter.filter}
                                </s-stack>
                            ))}
                            {chips.length > 0 && (
                                <s-stack direction="inline">
                                    <s-button variant="tertiary" onClick={onClearAll}>Clear all</s-button>
                                </s-stack>
                            )}
                        </s-stack>
                    </s-box>
                </s-popover>
            )}

            <s-popover id={sortPopoverId}>
                <s-box padding="base">
                    <s-choice-list label="Sort by" values={sortSelected.slice(0, 1)}
                        onChange={event => onSort(choiceValues(event).slice(0, 1))}>
                        {sortOptions.map(option => (
                            <s-choice key={option.value} value={option.value}>
                                {option.directionLabel ? `${option.label}: ${option.directionLabel}` : option.label}
                            </s-choice>
                        ))}
                    </s-choice-list>
                </s-box>
            </s-popover>

            {chips.length > 0 && (
                <s-stack direction="inline" gap="small-200" alignItems="center">
                    {chips.map(filter => <AppliedFilterChip key={filter.key} filter={filter} />)}
                    <s-button variant="tertiary" onClick={onClearAll}>Clear all</s-button>
                </s-stack>
            )}
        </s-stack>
    );
};

export default MappingFilters;
